import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Check, X, Mail } from 'lucide-react';

export const Pricing = () => {
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly' or 'annual'

  const plans = [
    {
      name: 'Gratis',
      description: 'Para probar TuAsistencia',
      price: { monthly: 0, annual: 0 },
      popular: false,
      features: [
        { text: 'Hasta 1 aula', included: true },
        { text: 'Hasta 40 estudiantes', included: true },
        { text: 'Registro de asistencia diaria', included: true },
        { text: 'Invitaciones por link', included: true },
        { text: 'Reportes básicos', included: true },
        { text: 'Exportación de reportes', included: false },
        { text: 'Envío a coordinación', included: false },
        { text: 'Analítica avanzada', included: false },
        { text: 'Soporte prioritario', included: false }
      ],
      cta: 'Empezar gratis',
      ctaAction: 'https://app.tuasistencia.com.co'
    },
    {
      name: 'Docente Pro',
      description: 'Para docentes individuales',
      price: { monthly: 9900, annual: 99000 },
      popular: true,
      features: [
        { text: 'Aulas ilimitadas', included: true },
        { text: 'Estudiantes ilimitados', included: true },
        { text: 'Registro de asistencia diaria', included: true },
        { text: 'Invitaciones por link ilimitadas', included: true },
        { text: 'Reportes avanzados por periodo', included: true },
        { text: 'Exportación Excel y PDF', included: true },
        { text: 'Analítica de tendencias', included: true },
        { text: 'Historial completo', included: true },
        { text: 'Soporte por email', included: true }
      ],
      cta: 'Comenzar prueba',
      ctaAction: 'https://app.tuasistencia.com.co'
    },
    {
      name: 'Panel de Coordinación',
      description: 'Para coordinadores académicos',
      price: { monthly: 12900, annual: 129000 },
      popular: false,
      badge: 'Complemento',
      finePrint: 'Panel para supervisar múltiples docentes. No incluye licencias de docentes.',
      features: [
        { text: 'Se conecta a las cuentas de docentes para ver reportes consolidados.', included: true },
        { text: 'Todo en Docente Pro', included: true },
        { text: 'Panel multi-docente', included: true },
        { text: 'Reportes consolidados institucionales', included: true },
        { text: 'Analítica institucional avanzada', included: true },
        { text: 'Gestión de permisos y roles', included: true },
        { text: 'Dashboard administrativo', included: true },
        { text: 'Administración de docentes', included: true },
        { text: 'Soporte prioritario', included: true },
        { text: 'Capacitación inicial', included: true }
      ],
      cta: 'Contáctanos',
      ctaAction: 'mailto:contacto@bitakora.com.co'
    }
  ];

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const getDisplayPrice = (plan) => {
    const price = billingPeriod === 'monthly' ? plan.price.monthly : plan.price.annual;
    if (price === 0) return 'Gratis';
    
    if (billingPeriod === 'annual') {
      const monthlyEquivalent = price / 12;
      return (
        <div>
          <div className="text-4xl font-bold">{formatPrice(monthlyEquivalent)}</div>
          <div className="text-sm text-gray-500 mt-1">
            {formatPrice(price)} facturado anualmente
          </div>
        </div>
      );
    }
    
    return <div className="text-4xl font-bold">{formatPrice(price)}</div>;
  };

  const getSavingsPercentage = (plan) => {
    if (plan.price.monthly === 0) return null;
    const annualMonthly = plan.price.annual / 12;
    const savings = ((plan.price.monthly - annualMonthly) / plan.price.monthly) * 100;
    return Math.round(savings);
  };

  return (
    <section id="planes" className="py-20 lg:py-28 bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Planes que se ajustan a{' '}
            <span className="text-blue-600">tus necesidades</span>
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Comienza gratis y escala cuando lo necesites
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-white rounded-full p-1 shadow-sm border border-gray-200 mb-10">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-200 ${
                billingPeriod === 'monthly'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Mensual
            </button>
            <button
              onClick={() => setBillingPeriod('annual')}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-200 relative ${
                billingPeriod === 'annual'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Anual
              <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                -17%
              </span>
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {plans.map((plan, index) => {
            const savings = getSavingsPercentage(plan);
            
            return (
              <div
                key={index}
                className={`relative bg-white rounded-3xl p-8 flex flex-col ${
                  plan.popular
                    ? 'border-2 border-blue-600 shadow-xl scale-105 lg:scale-110'
                    : 'border border-gray-200 shadow-sm'
                }`}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-6 py-1.5 rounded-full text-sm font-semibold shadow-lg">
                    Más popular
                  </div>
                )}

                {/* Badge for Coordination */}
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-teal-600 text-white px-6 py-1.5 rounded-full text-sm font-semibold shadow-lg">
                    {plan.badge}
                  </div>
                )}

                {/* Plan Header */}
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {plan.description}
                  </p>
                </div>

                {/* Price */}
                <div className="mb-6">
                  {getDisplayPrice(plan)}
                  {plan.price.monthly > 0 && (
                    <div className="text-gray-500 text-sm mt-2">
                      por {billingPeriod === 'monthly' ? 'mes' : 'mes (pago anual)'}
                    </div>
                  )}
                  {plan.finePrint && (
                    <div className="text-gray-500 text-xs mt-2 leading-snug">
                      {plan.finePrint}
                    </div>
                  )}
                  {billingPeriod === 'annual' && savings && (
                    <div className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">
                      Ahorras {savings}% anualmente
                    </div>
                  )}
                </div>

                {/* CTA Button */}
                <Button
                  size="lg"
                  onClick={() => {
                    if (plan.ctaAction.startsWith('mailto:')) {
                      window.location.href = plan.ctaAction;
                    } else if (plan.ctaAction.startsWith('http://') || plan.ctaAction.startsWith('https://')) {
                      window.location.href = plan.ctaAction;
                    } else {
                      navigate(plan.ctaAction);
                    }
                  }}
                  className={`w-full mb-6 font-semibold ${
                    plan.popular
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                >
                  {plan.cta === 'Contáctanos' && <Mail size={18} className="mr-2" />}
                  {plan.cta}
                </Button>

                {/* Note for Coordination Plan */}
                {plan.note && (
                  <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-800 text-center">
                      {plan.note}
                    </p>
                  </div>
                )}

                {/* Features */}
                <div className="space-y-4 flex-grow">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className={`mt-0.5 ${feature.included ? 'text-green-600' : 'text-gray-300'}`}>
                        {feature.included ? <Check size={20} strokeWidth={3} /> : <X size={20} />}
                      </div>
                      <span className={feature.included ? 'text-gray-700' : 'text-gray-400'}>
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="max-w-3xl mx-auto text-center mt-12">
          <p className="text-gray-600">
            ¿Necesitas un plan personalizado para tu institución?{' '}
            <a
              href="mailto:contacto@bitakora.com.co"
              className="text-blue-600 hover:text-blue-700 font-medium underline"
            >
              Contáctanos
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};
